/**
 * Mi jornada.
 *
 * Horas de hoy, el turno que toca y los fichajes del día.
 *
 * No hay botón de editar: hay botón de PEDIR corrección. Que la interfaz lo
 * diga en voz alta no es decorativo — es la diferencia entre un registro que
 * vale ante una inspección y uno que no.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  apiClient,
  getTodaySummary,
  getMyPlanning,
  requestCorrection,
  DailySummary,
  MyPlanningItem,
} from '../services/apiClient';
import { Icon } from './ui/Icon';

function hhmm(hours: number): string {
  // Minutos TOTALES con módulo 60: redondear sólo la parte fraccionaria
  // sacaba m = 60 en cualquier fracción ≥ 59,5 min (7,995 h → «7:60») y
  // el trabajador veía una jornada imposible donde compara con su contrato
  // (ronda 3, r2b19[4]).
  const total = Math.round(hours * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

const PUNCH_TYPES = [
  // ⚠️ 'IN'/'OUT', que es lo que vale el ENUM `punchtype` del backend y lo que
  // devuelve `GET /corrections/pending`. La pantalla mandaba
  // 'PUNCH_IN'/'PUNCH_OUT' —el vocabulario del SYNC, que es otro— y Pydantic
  // los rechazaba: 422 en el 100% de las solicitudes, con el interceptor
  // enseñando "Error del servidor" porque el detalle de un 422 es un array.
  // La mitad de aprobación de la bandeja funcionaba; la mitad de PETICIÓN, la
  // que da sentido a "no se edita, se corrige", era inoperante desde la única
  // pantalla que la ofrece.
  { value: 'IN', label: 'Entrada' },
  { value: 'OUT', label: 'Salida' },
] as const;

/** Hoy en el reloj del trabajador, no en UTC. */
function hoyLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

// Espejo mínimo de CorrectionResponse (backend/app/schemas/correction.py):
// para saber si el día tiene una corrección pendiente sólo hace falta el día.
interface PendingCorrectionRow {
  correction_date: string; // YYYY-MM-DD
}

export function MyPlanning() {
  const [day, setDay] = useState<DailySummary | null>(null);
  const [planning, setPlanning] = useState<MyPlanningItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  // `GET /timesheet/today` SIEMPRE devuelve un resumen (con `first_in` null
  // si no hay fichajes): `day === null` sólo puede significar "no se pudo
  // cargar". Antes ese null se pintaba como "Todavía no has fichado hoy" —
  // la pantalla afirmaba un hecho ("no has fichado") que no sabía si era
  // cierto, con el trabajador re-fichando la entrada que sí había hecho
  // (ronda 3, r2b19). Mientras no cargue, no se afirma nada del día.
  const [loadFailed, setLoadFailed] = useState(false);

  const [asking, setAsking] = useState(false);
  // Días con corrección PENDIENTE del propio usuario. Antes un `sent` que
  // nadie volvía a false escondía el botón para siempre tras la primera
  // solicitud: pedir la segunda corrección (entrada Y salida olvidadas el
  // mismo día) exigía desmontar la pantalla (ronda 3, r2b19[5]). Ahora el
  // botón se esconde sólo mientras el día que se mira tenga una pendiente —
  // resuelta (aprobada o rechazada), vuelve. Ojo: `GET /corrections/pending`
  // es de responsable; la lista del trabajador es `GET /corrections`.
  const [pendingDays, setPendingDays] = useState<ReadonlySet<string>>(new Set<string>());
  const [punchType, setPunchType] = useState<'IN' | 'OUT'>('IN');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');
  // El DÍA que se corrige. Se fijaba solo a "hoy" (con fallback en UTC, ni
  // siquiera local) y el formulario no tenía campo de fecha: quien el día 10
  // pedía la salida que olvidó el día 9 registraba una corrección DEL 10, el
  // responsable la aprobaba —la bandeja mostraba el 10, coherente y nada
  // sospechoso— y el ledger inmutable ganaba una salida del día 10 que nunca
  // ocurrió, mientras el 9 se quedaba con la jornada abierta. El informe del
  // art. 34.9 imprime esa hora como cierta.
  const [correctionDate, setCorrectionDate] = useState('');

  // Los días con corrección pendiente, para que el botón reaparezca cuando
  // el responsable resuelva. A fails-open: si la lista no llega, no se
  // bloquea pedir correcciones — sólo se pierde el aviso hasta la próxima
  // carga.
  const refreshPending = useCallback(async () => {
    try {
      const res = await apiClient.get<PendingCorrectionRow[]>('/corrections', {
        params: { status: 'PENDING' },
      });
      setPendingDays(new Set(res.data.map((c) => c.correction_date)));
    } catch {
      // silencio deliberado: ver comentario arriba
    }
  }, []);

  const load = useCallback(async () => {
    setError(null);
    setLoadFailed(false);
    const [summary, plan] = await Promise.all([
      getTodaySummary().catch(() => null),
      getMyPlanning().catch(() => null),
    ]);
    setDay(summary);
    setPlanning(plan ?? []);
    void refreshPending();
    if (summary === null) {
      // El resumen del día no llegó: banner con reintento, no silencio. El
      // `.catch(() => null)` hacía inalcanzable el catch del useEffect y el
      // error era invisible.
      setLoadFailed(true);
      setError('No se pudo cargar tu jornada. Comprueba la conexión.');
    }
  }, [refreshPending]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    setError(null);
    try {
      await requestCorrection({
        correction_date: correctionDate,
        punch_type: punchType,
        proposed_time: time,
        reason: reason.trim(),
      });
      setAsking(false);
      setReason('');
      setTime('');
      setCorrectionDate('');
      // Con la lista refrescada, el banner/botón reflejan la pendiente real:
      // si la corrección era del día que se mira, aparece el aviso; si era de
      // otro día, el botón sigue a mano para pedir la siguiente.
      await refreshPending();
    } catch (e: unknown) {
      const detail = (e as { apiError?: { message: string } })?.apiError?.message;
      setError(detail ?? 'No se pudo enviar la solicitud');
    }
  };

  // `GET /rostering/my-planning` devuelve una LISTA de días, no un objeto con
  // `today_shift`: leerlo sobre un array daba siempre `undefined` y el banner
  // "Turno de hoy" no se pintaba nunca — sin error y sin log, con el
  // trabajador creyendo que no tiene turno asignado.
  const shift = planning.find((p) => p.date === hoyLocal());
  // El día que se está mirando (y con el que se precarga la corrección).
  const viewDay = day?.date ?? hoyLocal();
  // El backend exige al menos 10 caracteres de motivo (CorrectionCreate). Y
  // no se apila una segunda pendiente sobre un día que ya tiene una: hasta
  // que el responsable no resuelva la primera, la segunda no tiene contra
  // qué revisarse.
  const canSend =
    correctionDate !== '' &&
    time !== '' &&
    reason.trim().length >= 10 &&
    !pendingDays.has(correctionDate);

  return (
    <div className="p-4 space-y-4">
      {error && (
        <div className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14.5px] flex items-center justify-between gap-3">
          <span>{error}</span>
          {loadFailed && (
            <button onClick={() => load()} className="shrink-0 font-semibold underline">
              Reintentar
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2.5">
        <div className="card-surface px-3 py-3.5">
          <p className="font-mono text-[10.5px] uppercase text-ink-3">Trabajado</p>
          <p className="font-display font-extrabold text-[23px]">
            {day ? hhmm(day.total_worked_hours) : '—'}
          </p>
        </div>
        <div className="card-surface px-3 py-3.5">
          <p className="font-mono text-[10.5px] uppercase text-ink-3">Presencia</p>
          <p className="font-display font-extrabold text-[23px]">
            {day ? hhmm(day.total_presence_hours) : '—'}
          </p>
        </div>
        <div className="card-surface px-3 py-3.5">
          <p className="font-mono text-[10.5px] uppercase text-ink-3">Pausas</p>
          <p className="font-display font-extrabold text-[23px]">
            {day ? `${day.breaks_paid_minutes + day.breaks_unpaid_minutes}′` : '—'}
          </p>
        </div>
      </div>

      {shift && (
        <div className="flex items-center gap-3 rounded-2xl bg-brand-soft border border-line-strong px-4 py-3.5">
          <Icon name="calendar" size={22} className="text-brand shrink-0" strokeWidth={1.8} />
          <div className="min-w-0">
            <p className="font-mono text-[12px] uppercase text-brand">Turno de hoy</p>
            <p className="font-semibold text-[15.5px] truncate">
              {shift.expected_hours} h
              {(shift.worksite as { name?: string })?.name
                ? ` · ${(shift.worksite as { name?: string }).name}`
                : ''}
            </p>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2.5">
          <p className="font-mono text-[12px] uppercase tracking-wider text-ink-3">Hoy</p>
          <p className="font-mono text-[11px] text-ink-3">no editables</p>
        </div>

        <div className="rounded-2xl bg-surface border border-line overflow-hidden">
          {day?.first_in ? (
            <>
              <div className="flex items-center gap-3 px-4 py-3.5">
                <span className="w-2.5 h-2.5 rounded-full bg-brand shrink-0" />
                <span className="font-mono text-[14px]">{day.first_in.slice(0, 5)}</span>
                <span className="flex-1 text-[14.5px]">Primera entrada</span>
              </div>
              <div className="h-px bg-line" />
              <div className="flex items-center gap-3 px-4 py-3.5">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: day.last_out ? 'var(--danger)' : 'var(--ink-3)' }}
                />
                <span className="font-mono text-[14px]">
                  {day.last_out ? day.last_out.slice(0, 5) : '—'}
                </span>
                <span className="flex-1 text-[14.5px]">
                  {day.last_out ? 'Última salida' : 'Jornada abierta'}
                </span>
              </div>
            </>
          ) : loadFailed ? (
            <p className="text-center text-[14px] text-ink-3 py-8">No se pudo comprobar el día</p>
          ) : (
            <p className="text-center text-[14px] text-ink-3 py-8">Todavía no has fichado hoy</p>
          )}
        </div>

        {pendingDays.has(viewDay) ? (
          <p className="mt-3.5 rounded-xl bg-brand-soft text-brand px-4 py-3 text-[14.5px]">
            Corrección pendiente de revisión. Cuando tu responsable la resuelva, podrás pedir
            otra para este día.
          </p>
        ) : asking ? (
          <div className="mt-3.5 card-surface space-y-3">
            <label className="block">
              <span className="label">Qué día</span>
              <input
                type="date"
                value={correctionDate}
                max={hoyLocal()}
                onChange={(e) => setCorrectionDate(e.target.value)}
                className="field"
              />
              {pendingDays.has(correctionDate) && (
                <span className="text-[12px] text-ink-3">
                  Ya tienes una corrección pendiente para ese día: espera a que se resuelva para
                  pedir otra.
                </span>
              )}
            </label>
            <div className="grid grid-cols-2 gap-2.5">
              <label className="block">
                <span className="label">Qué fichaje</span>
                <select
                  value={punchType}
                  onChange={(e) => setPunchType(e.target.value as 'IN' | 'OUT')}
                  className="field"
                >
                  {PUNCH_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="label">Hora correcta</span>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="field" />
              </label>
            </div>
            <label className="block">
              <span className="label">Qué pasó</span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Olvidé fichar al entrar; llegué a las 8:00."
                className="field h-auto py-2.5"
              />
              {reason.trim().length > 0 && reason.trim().length < 10 && (
                <span className="text-[12px] text-ink-3">Explícalo un poco más (mínimo 10 caracteres).</span>
              )}
            </label>
            <div className="flex gap-2.5">
              <button onClick={() => setAsking(false)} className="btn-ghost">
                Cancelar
              </button>
              <button onClick={send} disabled={!canSend} className="btn-primary h-12 text-[15px]">
                Enviar
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => {
              // Se precarga con el día que se está mirando, pero se puede
              // cambiar: es el dato del que depende dónde cae la marca.
              setCorrectionDate(viewDay);
              setAsking(true);
            }}
            className="btn-ghost mt-3.5 flex items-center justify-center gap-2.5"
          >
            <Icon name="edit" size={18} strokeWidth={1.8} />
            Solicitar corrección
          </button>
        )}

        <p className="font-mono text-[11.5px] text-ink-3 text-center mt-3 leading-relaxed">
          Un fichaje nunca se edita. Pides una corrección y un responsable la aprueba: queda como
          registro nuevo.
        </p>
      </div>
    </div>
  );
}
